/*
 * Copyright (C) 2024 Webformat S.r.l.
 * https://www.webformat.com
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import {
    Agreement,
    Cart,
    CheckoutStep,
    CmsBlock,
    CountryData,
    Coupon,
    Customer,
    GiftCard,
    PaymentDescription,
    PaymentMethod,
    QuoteAddress,
    ShippingFree,
    ShippingMethod,
    ShippingOptions,
    ShippingTimes,
    Totals,
} from '@/store/models';
import {clone, merge} from 'lodash';
import addressNormalizer from '@/commons/addressNormalizer';
import {setJWT} from '@/store/api';

// eslint-disable-next-line no-underscore-dangle
const INITIAL_STATE = window.__INITIAL_STATE__ || {};

const customer: Customer = {
    id: 0,
    group_id: 0,
    default_billing: '',
    default_shipping: '',
    email: '',
    firstname: '',
    lastname: '',
    addresses: [],
    extension_attributes: {
        is_subscribed: false,
        pu_customer_group: '',
        pu_customer_loyalty_group_date: '',
        pu_customer_points_balance: '',
        pu_employee: '',
        hipay_tokenized_cards: [],
        wfpaypal_vaults: [],
    },
};

const coupon: Coupon = {
    coupon: '',
    applied: false,
    error: '',
};

const selectedPayment = '';

const shippingMethods: ShippingMethod[] = [];

const shippingCountries: string[] = [];

const paymentMethods: PaymentMethod[] = [];

const paymentDescriptions: PaymentDescription[] = [];

const selectedShipment = '';

const address: QuoteAddress = {
    id: 0,
    region: '',
    region_id: 0,
    country_id: 'IT',
    street: [''],
    company: '',
    telephone: '',
    postcode: '',
    city: '',
    firstname: '',
    lastname: '',
    default_shipping: false,
    default_billing: false,
    same_as_billing: 0,
    save_in_address_book: 0,
    extension_attributes: {},
};

const steps: CheckoutStep[] = [
    {
        name: 'login',
        active: true,
        unlocked: true,
    },
    {
        name: 'shipping',
        active: false,
        unlocked: false,
    },
    {
        name: 'invoice',
        active: false,
        unlocked: false,
    },
    {
        name: 'confirm',
        active: false,
        unlocked: false,
    },
];

const quoteAddress = clone(address) as QuoteAddress;
quoteAddress.save_in_address_book = 0;
quoteAddress.same_as_billing = 0;

const billingAddress: QuoteAddress = addressNormalizer(quoteAddress) as QuoteAddress;

const shippingAddress: QuoteAddress = addressNormalizer(quoteAddress) as QuoteAddress;

const checkoutEmail = '';

const cart: Cart = {
    id: 0,
    is_active: true,
    is_virtual: false,
    items: [],
    items_count: 0,
    items_qty: 0,
    orig_order_id: 0,
    currency: {
        global_currency_code: '',
        base_currency_code: '',
        store_currency_code: '',
        quote_currency_code: '',
        store_to_base_rate: 0,
        store_to_quote_rate: 0,
        base_to_global_rate: 0,
        base_to_quote_rate: 0,
    },
    customer_is_guest: true,
    customer_tax_class_id: 0,
    store_id: 0,
    billing_address: undefined,
    shipping_address: undefined,
};

const totals: Totals = {
    grand_total: 0,
    base_grand_total: 0,
    subtotal: 0,
    base_subtotal: 0,
    discount_amount: 0,
    base_discount_amount: 0,
    subtotal_with_discount: 0,
    base_subtotal_with_discount: 0,
    shipping_amount: 0,
    base_shipping_amount: 0,
    shipping_discount_amount: 0,
    base_shipping_discount_amount: 0,
    tax_amount: 0,
    base_tax_amount: 0,
    weee_tax_applied_amount: 0,
    shipping_tax_amount: 0,
    base_shipping_tax_amount: 0,
    subtotal_incl_tax: 0,
    shipping_incl_tax: 0,
    base_shipping_incl_tax: 0,
    base_currency_code: '',
    quote_currency_code: '',
    items_qty: 0,
    total_segments: [],
};

const giftCard: GiftCard = {
    code: '',
    applied: false,
    verified: false,
    credit: 0,
    message: '',
};

const settings = JSON.parse(process.env.VUE_APP_STORE_OPTIONS);

const token: string = '';

const cartToken: string = '';

const cmsblocks: CmsBlock[] = [];

const agreements: Agreement[] = [];

const countrydata: CountryData = {
    countries: [],
    regions: [],
    prefixes: {},
    defaultCountry: '',
    associations: {},
};

const paymentSuccessInfo = {};

const shippingOptions: ShippingOptions = {
    notice: {
        selected: false,
    },
    skip: {
        selected: false,
    },
};

const shippingFree: ShippingFree = {
    label: '',
    title: '',
    description: '',
};

const shippingTimes: ShippingTimes = {
    title: '',
    description: '',
};

const requestInvoice: any = {
    requested: false,
    novatrequested: 0,
};

const stateObj: any = {
    customer,
    cart,
    totals,
    address,
    coupon,
    selectedPayment,
    shippingMethods,
    shippingCountries,
    paymentMethods,
    paymentDescriptions,
    selectedShipment,
    billingAddress,
    shippingAddress,
    checkoutEmail,
    steps,
    token,
    cartToken,
    cmsblocks,
    agreements,
    countrydata,
    settings,
    giftCard,
    paymentSuccessInfo,
    shippingOptions,
    shippingFree,
    shippingTimes,
    requestInvoice,
};

export function initState(key: string) {
    if (typeof INITIAL_STATE[key] === 'string') {
        if (key === 'token' && INITIAL_STATE[key] !== '') {
            setJWT(INITIAL_STATE[key]);
        }
        return INITIAL_STATE[key];
    }
    return merge(stateObj[key], INITIAL_STATE[key] || {});
}

export function blankState(key: string) {
    return stateObj[key];
}
